import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useTheme,
  useMediaQuery,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Skeleton,
  Container,
  Collapse,
  IconButton
} from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import LoadingPage from '../components/LoadingPage';

const Devices = () => {
  const { accessToken, isAdmin, isSuperuser } = useAuth();
  const navigate = useNavigate();
  const [allDevices, setAllDevices] = useState([]);
  const [devices, setDevices] = useState([]);
  const [sites, setSites] = useState(new Map());
  const [groups, setGroups] = useState(new Map());
  const [groupDetails, setGroupDetails] = useState([]); // Store full group objects for site association
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDevicesOpen, setIsDevicesOpen] = useState(true);
  const [filters, setFilters] = useState({
    device_id: '',
    name: '',
    site_id: '',
    site_name: '',
    group_name: ''
  });

  const baseURL = process.env.REACT_APP_BASEURL;

  const theme = useTheme();
  const isXsScreen = useMediaQuery(theme.breakpoints.down('sm'));

  // Fetch devices, sites, and groups on mount or when accessToken changes
  useEffect(() => {
    const fetchDevices = async () => {
      setLoading(true);
      setError(null);
      try {
        // Fetch devices
        const deviceResponse = await axios.get(`${baseURL}/devices`, {
          headers: { Authorization: `Bearer ${accessToken}` },
          params: { with_details: true }
        });
        const fetchedDevices = deviceResponse.data || [];
        setAllDevices(fetchedDevices);

        // Fetch sites
        const sitesResponse = await axios.get(`${baseURL}/get-sites`, {
          headers: { Authorization: `Bearer ${accessToken}` }
        });
        const fetchedSites = sitesResponse.data.data || [];
        setSites(new Map(fetchedSites.map(site => [String(site.id), site.name || `Site ${site.id}`])));

        // Fetch groups
        const groupsResponse = await axios.get(`${baseURL}/groups`, {
          headers: { Authorization: `Bearer ${accessToken}` }
        });
        const fetchedGroups = groupsResponse.data || [];
        setGroups(new Map(fetchedGroups.map(group => [String(group.id), group.name || `Group ${group.id}`])));
        setGroupDetails(fetchedGroups); // Store full group objects

        // Initially show all devices
        setDevices(fetchedDevices);
      } catch (error) {
        console.error('Data fetch error:', error.response ? error.response.data : error.message);
        setError('Failed to load devices, sites, or groups. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchDevices();
  }, [accessToken]);

  // Set initial filter values after sites is fetched
  useEffect(() => {
    if (sites.size > 0 && filters.site_name === '') {
      const sortedSites = [...sites.entries()].sort((a, b) => {
        const nameA = a[1] || `Site ${a[0]}`;
        const nameB = b[1] || `Site ${b[0]}`;
        return nameA.localeCompare(nameB) || a[0] - b[0];
      });
      const [firstSiteId, firstSiteName] = sortedSites[0];
      setFilters(prev => ({
        ...prev,
        site_id: firstSiteId,
        site_name: firstSiteName
      }));
    }
  }, [sites]);

  // Filter devices locally whenever filters change
  useEffect(() => {
    const filteredDevices = allDevices.filter(device => {
      if (device.device_type && ['base', 'echo'].includes(device.device_type.toLowerCase())) {
        return false;
      }

      const deviceIdMatch = filters.device_id
        ? String(device.id) === String(filters.device_id)
        : true;
      const nameMatch = filters.name
        ? device.name?.toLowerCase().includes(filters.name.toLowerCase())
        : true;
      const siteIdMatch = filters.site_id
        ? String(device.site) === String(filters.site_id)
        : true;
      const siteNameMatch = filters.site_name
        ? (sites.get(String(device.site)) || `Site ${device.site}`)
            .toLowerCase()
            .includes(filters.site_name.toLowerCase())
        : true;
      const groupNameMatch = filters.group_name
        ? device.groups.some(groupId => 
            (groups.get(String(groupId)) || `Group ${groupId}`)
              .toLowerCase()
              .includes(filters.group_name.toLowerCase()))
        : true;

      return deviceIdMatch && nameMatch && siteIdMatch && siteNameMatch && groupNameMatch;
    });

    setDevices(filteredDevices);
  }, [filters, allDevices, sites, groups]);

  // Handle filter changes and synchronize Site ID, Site Name, Group Name, and Device ID
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => {
      const newFilters = { ...prev, [name]: value };

      if (name === 'site_id' && value) {
        const siteName = sites.get(String(value)) || `Site ${value}`;
        newFilters.site_name = siteName;
        // Reset group_name and device_id if they are not valid for the new site
        if (newFilters.group_name) {
          const selectedGroup = groupDetails.find(group => 
            (group.name || `Group ${group.id}`) === newFilters.group_name && 
            String(group.site) === String(value)
          );
          newFilters.group_name = selectedGroup ? newFilters.group_name : '';
        }
        if (newFilters.device_id) {
          const selectedDevice = allDevices.find(device => 
            String(device.id) === String(newFilters.device_id) && 
            String(device.site) === String(value)
          );
          newFilters.device_id = selectedDevice ? newFilters.device_id : '';
        }
      } else if (name === 'site_id' && !value) {
        newFilters.site_name = '';
        // Reset group_name and device_id when site is cleared
        newFilters.group_name = '';
        newFilters.device_id = '';
      } else if (name === 'site_name' && value) {
        const siteEntry = [...sites.entries()].find(
          ([id, name]) => name === value
        );
        newFilters.site_id = siteEntry ? String(siteEntry[0]) : '';
        // Reset group_name and device_id if they are not valid for the new site
        if (newFilters.group_name && newFilters.site_id) {
          const selectedGroup = groupDetails.find(group => 
            (group.name || `Group ${group.id}`) === newFilters.group_name && 
            String(group.site) === String(newFilters.site_id)
          );
          newFilters.group_name = selectedGroup ? newFilters.group_name : '';
        }
        if (newFilters.device_id && newFilters.site_id) {
          const selectedDevice = allDevices.find(device => 
            String(device.id) === String(newFilters.device_id) && 
            String(device.site) === String(newFilters.site_id)
          );
          newFilters.device_id = selectedDevice ? newFilters.device_id : '';
        }
      } else if (name === 'site_name' && !value) {
        newFilters.site_id = '';
        newFilters.group_name = '';
        newFilters.device_id = '';
      }

      return newFilters;
    });
  };

  const handleFilterSubmit = (e) => {
    e.preventDefault();
  };

  const handleClearFilters = () => {
    setFilters({
      device_id: '',
      name: '',
      site_id: '',
      site_name: '',
      group_name: ''
    });
  };

  const handleViewStatistics = (deviceId, siteId) => {
    navigate('/dashboard/statistics', { state: { deviceId, siteId } });
  };

  const LoadingSkeleton = () => (
    <Container maxWidth="xl" disableGutters sx={{ p: 0 }}>
      <Box sx={{ p: 0 }}>
        <Typography variant="h4" gutterBottom>
          <Skeleton width="30%" height={40} />
        </Typography>
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 2 }}>
            {Array(5).fill().map((_, index) => (
              <Skeleton key={index} variant="rectangular" width={isXsScreen ? '100%' : 150} height={56} />
            ))}
          </Box>
          <Skeleton variant="rectangular" width={isXsScreen ? '100%' : 120} height={40} />
        </Box>
        <Paper sx={{ p: { xs: 1, sm: 2 } }}>
          <Typography variant="h6" gutterBottom>
            <Skeleton width="20%" height={32} />
          </Typography>
          <Box sx={{ overflowX: 'auto' }}>
            <Table sx={{ minWidth: { xs: 300, sm: 1000 } }}>
              <TableHead>
                <TableRow>
                  {['ID', 'Name', 'Device Type', 'Site', 'Groups', 'Last Measurement', 'Last Seen', 'Status', 'Packets/Day'].map((header) => (
                    <TableCell key={header}><Skeleton /></TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {Array(3).fill().map((_, rowIndex) => (
                  <TableRow key={rowIndex}>
                    {Array(9).fill().map((_, cellIndex) => (
                      <TableCell key={cellIndex}><Skeleton /></TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Paper>
      </Box>
    </Container>
  );

  // if (loading) return <LoadingSkeleton />;
  if (loading) return <LoadingPage />;

  if (error) return (
    <Container maxWidth="xl">
      <Box sx={{ p: { xs: 1, sm: 2 } }}>
        <Typography color="error">{error}</Typography>
      </Box>
    </Container>
  );

  // Filter groups and device IDs based on selected site
  const filteredGroups = filters.site_id
    ? groupDetails
        .filter(group => String(group.site) === String(filters.site_id))
        .map(group => group.name || `Group ${group.id}`)
    : [...new Set(groups.values())];
  const uniqueGroupNames = [...new Set(filteredGroups)].sort();

  const filteredDeviceIds = filters.site_id
    ? allDevices
        .filter(device => String(device.site) === String(filters.site_id) && !['base', 'echo'].includes(device.device_type?.toLowerCase()))
        .map(device => String(device.id))
    : [...new Set(allDevices.map(device => String(device.id)))];
  const uniqueDeviceIds = [...new Set(filteredDeviceIds)].sort();

  return (
    <Container maxWidth="xl" disableGutters sx={{ p: 0 }}>
      <Box sx={{ p: 0 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
          <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: '1.75rem', md: '2.125rem' } }}>
            Devices
          </Typography>
          {(isAdmin || isSuperuser) && (
            <Box sx={{ display: "flex", justifyContent: "end" }}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => navigate("/devices/add")}
                sx={{
                  fontSize: { xs: "0.9rem", md: "1rem" },
                  textTransform: "none",
                }}
              >
                Add Device
              </Button>
            </Box>
          )}
        </Box>

        <Paper sx={{ p: { xs: 1, sm: 2 }, mb: 4 }}>
          <Typography variant="h6" gutterBottom sx={{ fontSize: { xs: '1.1rem', md: '1.25rem' } }}>
            Filter Devices
          </Typography>
          <Box
            component="form"
            onSubmit={handleFilterSubmit}
            sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 2 }}
          >
            <TextField
              label="Device Name"
              name="name"
              value={filters.name}
              onChange={handleFilterChange}
              variant="outlined"
              size="small"
              sx={{ minWidth: { xs: '100%', sm: 150 }, '& .MuiInputLabel-root': { color: 'black', fontWeight: 100 } }}
            />
            <FormControl variant="outlined" size="small" sx={{ minWidth: { xs: '100%', sm: 150 } }}>
              <InputLabel id="site-name-label" sx={{ color: 'black', fontWeight: 100 }}>Site Name</InputLabel>
              <Select
                labelId="site-name-label"
                name="site_name"
                value={filters.site_name}
                onChange={handleFilterChange}
                label="Site Name"
              >
                <MenuItem value=""><em>None</em></MenuItem>
                {[...new Set(sites.values())].sort().map(name => (
                  <MenuItem key={name} value={name}>{name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl variant="outlined" size="small" sx={{ minWidth: { xs: '100%', sm: 150 } }}>
              <InputLabel id="site-id-label" sx={{ color: 'black', fontWeight: 100 }}>Site ID</InputLabel>
              <Select
                labelId="site-id-label"
                name="site_id"
                value={filters.site_id}
                onChange={handleFilterChange}
                label="Site ID"
              >
                <MenuItem value=""><em>None</em></MenuItem>
                {[...sites.keys()].sort().map(id => (
                  <MenuItem key={id} value={id}>{id}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl variant="outlined" size="small" sx={{ minWidth: { xs: '100%', sm: 150 } }}>
              <InputLabel id="group-name-label" sx={{ color: 'black', fontWeight: 100 }}>Group Name</InputLabel>
              <Select
                labelId="group-name-label"
                name="group_name"
                value={filters.group_name}
                onChange={handleFilterChange}
                label="Group Name"
              >
                <MenuItem value=""><em>None</em></MenuItem>
                {uniqueGroupNames.map(name => (
                  <MenuItem key={name} value={name}>{name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl variant="outlined" size="small" sx={{ minWidth: { xs: '100%', sm: 150 } }}>
              <InputLabel id="device-id-label" sx={{ color: 'black', fontWeight: 100 }}>Device ID</InputLabel>
              <Select
                labelId="device-id-label"
                name="device_id"
                value={filters.device_id}
                onChange={handleFilterChange}
                label="Device ID"
              >
                <MenuItem value=""><em>None</em></MenuItem>
                {uniqueDeviceIds.map(id => (
                  <MenuItem key={id} value={id}>{id}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button variant="outlined" color="secondary" onClick={handleClearFilters}>
              Clear Filters
            </Button>
          </Box>
        </Paper>

        <Paper sx={{ p: { xs: 1, sm: 2 } }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              p: 1,
              borderRadius: 1
            }}
            onClick={() => setIsDevicesOpen(!isDevicesOpen)}
          >
            <Typography
              variant="h6"
              sx={{ fontSize: { xs: '1.1rem', md: '1.25rem' }, flexGrow: 1 }}
            >
              Devices
            </Typography>
            <IconButton size="small">
              {isDevicesOpen ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>
          <Collapse in={isDevicesOpen}>
            {devices.length > 0 ? (
              <Box sx={{ overflowX: 'auto', mt: 2 }}>
                <Table sx={{ minWidth: { xs: 300, sm: 1000 } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Device Type</TableCell>
                      <TableCell>Site (ID)</TableCell>
                      <TableCell>Groups</TableCell>
                      <TableCell>Last Measurement</TableCell>
                      <TableCell>Last Seen</TableCell>
                      <TableCell>Status</TableCell>
                      {/* <TableCell>Packets/Day</TableCell> */}
                      <TableCell sx={{ textAlign: "center" }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {devices.map((device) => (
                      <TableRow 
                        key={device.id}
                        sx={{
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: 'grey.100', outline: '2px solid', outlineColor: theme.palette.primary.main },
                        }}
                      >
                        <TableCell>{device.id}</TableCell>
                        <TableCell>{device.name}</TableCell>
                        <TableCell>{device.device_type}</TableCell>
                        <TableCell>{sites.get(String(device.site)) ? `${sites.get(String(device.site))} (${device.site})` : device.site}</TableCell>
                        <TableCell>
                          {device.groups.length > 0 
                            ? device.groups.map(groupId => 
                                groups.get(String(groupId)) ? `${groups.get(String(groupId))} (${groupId})` : groupId
                              ).join(', ') 
                            : 'None'
                          }
                        </TableCell>
                        <TableCell sx={{ minWidth: 200 }}>
                          {device.last_measurement ? (
                            <Box>
                              <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                                Timestamp: {new Date(device.last_measurement.timestamp).toLocaleString()}
                              </Typography>
                              {/* <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                                Moisture: {(device.last_measurement.moisture * 100).toFixed(1)}%
                              </Typography>
                              <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                                Temperature: {device.last_measurement.temperature.toFixed(1)}°C
                              </Typography>
                              <Typography variant="body2" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                                Salinity: {device.last_measurement.salinity.toFixed(1)} dS/m
                              </Typography> */}
                            </Box>
                          ) : 'N/A'}
                        </TableCell>
                        <TableCell sx={{ minWidth: 120 }}>
                          {device.last_seen ? new Date(device.last_seen).toLocaleString() : 'N/A'}
                        </TableCell>
                        <TableCell>{device.device_status || 'N/A'}</TableCell>
                        {/* <TableCell>{device.packets_per_day}</TableCell> */}
                        <TableCell>
                          <Box sx={{ display: "flex", gap: "1rem" }}>
                            <Button
                              variant="contained"
                              color="secondary"
                              onClick={() => navigate(`/devices/edit-device/${device.id}`)}
                              sx={{
                                fontSize: { xs: "0.9rem", md: "1rem" },
                                textTransform: "none",
                                color: "white"
                              }}
                            >
                              Edit
                          </Button>
                            <Button
                              variant="contained"
                              color="primary"
                              onClick={() => handleViewStatistics(device.id, device.site)}
                              sx={{
                                fontSize: { xs: "0.9rem", md: "1rem" },
                                textTransform: "none",
                                color: "white",
                                minWidth: "fit-content"
                              }}
                            >
                              View Statistics
                          </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            ) : (
              <Typography variant="body2" sx={{ mt: 2 }}>
                No devices found matching the filters.
              </Typography>
            )}
          </Collapse>
        </Paper>
      </Box>
    </Container>
  );
};

export default Devices;